/* eslint-disable max-lines */
import React, { FC, useEffect, useState } from "react";
import { Redirect } from "react-router-dom";

import { PollingAddress, PollingAddressRequest } from "../../../../../models/pollingAddress";
import { getACDs, getGroups } from "../../../../../services/get.data.service";
import { emailRegEx } from "../../../../shared/constants";
import DateInput from "../../shared/fields/dateInput";
import EmailInput from "../../shared/fields/emailInput";
import SelectInput from "../../shared/fields/selectInput";
import TextInput from "../../shared/fields/textInput";
import ToggleInput from "../../shared/fields/toggleInput";
import SettingsFormInfo from "../../shared/settingsFormInfo";
import SettingsTitle from "../../shared/settingsTitle";
import { SelectOption } from "../../../../../models/selectOption";
import { dbDateStringToLocalDate } from "../../../../shared/helpers/dates";
import { GroupRule, ResponseData } from "../../../../../models";
import { putGroupRulePrecedence, putSetting } from "../../../../../services/put.data.service";
import { postSetting } from "../../../../../services/post.data.service";
import GroupRulesInput from "./groupRulesInput";
import SettingsSubmitButton from "../../shared/settingsSubmitButton";
import MailboxFoldersInput from "../../shared/fields/mailboxFoldersInput";
import { MailboxFolder } from "../../../../../models/mailboxFolder";

type Props = {
  isDuplicate: (value: string) => boolean;
  existingPollingAddress?: PollingAddress;
  load: () => void;
};

const PollingAddressForm: FC<Props> = ({ isDuplicate, existingPollingAddress: existing, load }) => {
  const [redirect, setRedirect] = useState<boolean>(false);
  const [clientOptions, setClientOptions] = useState<SelectOption[]>([]);
  const [groupOptions, setGroupOptions] = useState<SelectOption[]>([]);
  const [groupRules, setGroupRules] = useState<GroupRule[]>([]);
  const [activelyPolling, setActivelyPolling] = useState<boolean>(false);
  const [emailAddress, setEmailAddress] = useState<string>("");
  const [mailboxFolder, setMailboxFolder] = useState<MailboxFolder>({ name: "Inbox", id: "inbox" });
  const [clientId, setClientId] = useState<number | null>(null);

  const [groupId, setGroupId] = useState<number | null>(null);
  const [pollFrom, setPollFrom] = useState<Date>(new Date());
  const [pollFromChanged, setPollFromChanged] = useState<boolean>(false);
  const [caseRefPrefix, setCaseRefPrefix] = useState<string>("");
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (existing) {
      setActivelyPolling(existing.activelyPolling);
      setEmailAddress(existing.emailAddress);
      setMailboxFolder({ name: existing.mailboxFolderName ?? "Inbox", id: existing.mailboxFolderId ?? "inbox" });
      setGroupId(existing.groupId);
      setGroupRules(

        [...(existing.groupRules ?? [])].sort((a, b) => a.precedence - b.precedence)

      );

      setClientId(existing.clientId);
      setPollFrom(
        // correct bad dates in the DB
        existing.pollFrom && existing.pollFrom > "2020-01-01T00:00:00"
          ? dbDateStringToLocalDate(existing.pollFrom)
          : new Date()
      );
      setCaseRefPrefix(existing.caseRefPrefix);
    } else {
      setMailboxFolder({ name: "Inbox", id: "inbox" });
    }
    getACDs().then((clients) => {
      setClientOptions(
        clients.data.map((client) => {
          return {
            id: client.clientId,
            label: client.clientName,
          };
        })
      );
    });
    getGroups().then((groups) => {
      setGroupOptions(
        groups.data.map((group) => {
          return {
            id: group.groupId,
            label: group.businessName,
          };
        })
      );
    });
  }, [existing]);

  const validate = (): string[] => {
    const errorMessages: string[] = [];
    if (emailAddress.trim().length === 0) {
      errorMessages.push("Email Address is required");
    } else if (!emailRegEx.test(emailAddress.trim())) {
      errorMessages.push("Email is not a valid address");
    } else if (isDuplicate(`${emailAddress.trim()}|${mailboxFolder.id}`)) {
      errorMessages.push("Email Address / Folder has already been used");
    }
    if (groupId === null) {
      errorMessages.push("Default Group is required");
    }
    if (caseRefPrefix === null || !caseRefPrefix.match(/^[A-Z]{2}$/)) {
      errorMessages.push("Case Reference Prefix must be 2 letters");
    }
    return errorMessages;
  };

  const handleSaveResponse = (result: ResponseData<PollingAddressRequest>): void => {
    if (result.success) {
      load();
      setRedirect(true);
    } else {
      setErrors([`Failed to update. ${result.message || result}`]);
    }
  };

  const save = async (): Promise<void> => {
    const errorMessages = validate();
    setErrors(errorMessages);
    const toSave: PollingAddressRequest = {
      activelyPolling,
      emailAddress: emailAddress.trim(),
      mailboxFolderId: mailboxFolder.id,
      mailboxFolderName: mailboxFolder.name,
      clientId: clientId as number,
      groupId: groupId as number,
      caseRefPrefix: caseRefPrefix,
    };
    if (!errorMessages.length) {
      if (existing) {
        toSave.pollFrom = pollFromChanged ? pollFrom.toJSON() : undefined;
        return putSetting<PollingAddressRequest>("emailaccounts", existing.emailAccountId, toSave)
          .then(async (result: ResponseData<PollingAddressRequest>) => {
            if (!result.success) {
              handleSaveResponse(result);
              return;
            }

            if (groupRules.length === 0) {
              handleSaveResponse(result);
              return;
            }
            return putGroupRulePrecedence(existing.emailAccountId, groupRules.map((rule, index) => ({
              groupRuleId: rule.groupRuleId,
              precedence: index + 1
            })))
              .then((groupRuleSaveResult: ResponseData<boolean>) => {
                if (!groupRuleSaveResult.success) {
                  setErrors([`Failed to update Group Rule precedence. ${groupRuleSaveResult.message || groupRuleSaveResult}`]);
                  return;
                }

                handleSaveResponse(result);
              });
          });
      } else {
        toSave.pollFrom = pollFrom.toJSON();
        return postSetting<PollingAddressRequest>("emailaccounts", toSave)
          .then((result: ResponseData<PollingAddressRequest>) => {
            handleSaveResponse(result);
          });
      }
    }
  };

  if (redirect) return <Redirect to="/system-settings/emailaccounts/list" />;

  return (
    <>
      <SettingsTitle errors={errors} title={existing ? "Edit Email Account" : "Create Email Account"}>
        <SettingsSubmitButton onSave={save} />
      </SettingsTitle>
      <div className="row">
        <SettingsFormInfo existingTitle={existing?.emailAddress}>
          <p>
            Polling Emails After: {existing?.pollFrom
              ? pollFrom.toLocaleString()
              : "none"}
          </p>
          <p>Actively polling: {existing?.activelyPolling ? "Yes" : "No"}</p>
        </SettingsFormInfo>

        <div className="col-sm-7">
          <div className="content-container p-3">
            <ToggleInput
              label="Actively Polling"
              name="actively-polling"
              onChange={setActivelyPolling}
              value={activelyPolling}
            />
            <DateInput
              label="Polling Emails After"
              name="last-polled"
              onChange={(value): void | null => {
                if (value) {
                  setPollFrom(value);
                  setPollFromChanged(true);
                }
              }}
              value={pollFrom}
            />
            <EmailInput
              label="Email Address"
              name="email-address"
              onChange={(value): void => {
                setEmailAddress(value);
                setMailboxFolder({ name: "Inbox", id: "inbox" });
              }}
              value={emailAddress}
            />
            {emailAddress && emailRegEx.test(emailAddress.trim()) && (
              <MailboxFoldersInput
                allowInbox
                buttonText={`Poll folder other than ${mailboxFolder?.name || "Inbox"}`}
                emailAddress={emailAddress}
                mailboxFolder={mailboxFolder}
                setMailboxFolder={setMailboxFolder}
              />
            )}
            <TextInput
              label="Case Reference Prefix"
              name="unique-id"
              onChange={(id: string) => {
                if (id === "" || (id.length < 3 && id.match(/^[A-Z]*$/i))) {
                  setCaseRefPrefix(id.toUpperCase());
                }
              }}
              value={caseRefPrefix}
            />
            <SelectInput
              label="Client"
              name="client-id"
              onChange={setClientId}
              id={clientId}
              options={clientOptions}
            />
            
            {!!existing && !!groupRules.length && (
              <GroupRulesInput
                groupRules={groupRules}
                setGroupRules={setGroupRules}
              />
            )}
            
            <SelectInput
              label="Default Group"
              name="group-id"
              onChange={setGroupId}
              id={groupId}
              options={groupOptions}
            />
          </div>
        </div>
      </div>
    </>
  );
};
export default PollingAddressForm;
